<template>
  <div v-if="!loading" class="q-gutter-y-md parent-card">
    <q-dialog
      v-if="unitMixCsvImportDialog"
      v-model="unitMixCsvImportDialog"
      transition-duration="100"
    >
      <unit-mix-csv-import @finished="unitMixCsvImportFinished" />
    </q-dialog>

    <q-card bordered flat class="full-width">
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Enter New Development Information</div>
          <div class="text-caption">
            Trying to analyze an existing property?
            <router-link :to="{ name: 'New Property Report' }" class="text-primary">
              Click here
            </router-link>
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <address-google-autocomplete
          ref="autocompleteRef"
          :value="location.address"
          :filled="false"
          outlined
          @click="selectedAddress"
        />
        <div class="row q-mt-md q-gutter-x-md">
          <q-input v-model="buildingName" label="Property Name" outlined class="col" />
          <q-input
            ref="numberUnitsInput"
            v-model.number="numberUnits"
            label="Number of Units*"
            outlined
            no-error-icon
            lazy-rules
            :rules="[
              (val) => !!val || 'Number of Units is required',
              (val) => isInteger(val) || 'Number of Units must be an integer',
              (val) => val > 0 || 'Number of Units must be greater than 0',
              (val) => val < 20000 || 'Number of Units must be less than 20000'
            ]"
            class="col"
            @blur="getMarketStats"
          />
          <q-input
            ref="yearBuiltInput"
            v-model.number="yearBuilt"
            label="Year Built*"
            outlined
            no-error-icon
            lazy-rules
            :rules="[
              (val) => !!val || 'Year Built is required',
              (val) => isInteger(val) || 'Year Built must be an integer',
              (val) => val > 1800 || 'Year Built must be greater than 1800',
              (val) => val < 2030 || 'Year Built must be less than 2030'
            ]"
            class="col"
            @blur="getMarketStats"
          />
          <q-input
            ref="numberStoriesInput"
            v-model.number="numberStories"
            label="Number of Stories*"
            outlined
            no-error-icon
            lazy-rules
            :rules="[
              (val) => !!val || 'Number of Stories is required',
              (val) => isInteger(val) || 'Number of Stories must be an integer',
              (val) => val > 0 || 'Number of Stories must be greater than 0',
              (val) => val < 100 || 'Number of Stories must be less than 100'
            ]"
            class="col"
            @blur="getMarketStats"
          />
        </div>
      </q-card-section>
    </q-card>

    <q-card bordered flat class="full-width">
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Enter Unit Mix</div>
        </div>
        <q-btn
          no-caps
          :outline="$q.screen.gt.xs"
          class="q-mr-sm"
          @click="unitMixCsvImportDialog = true"
        >
          CSV Import
        </q-btn>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <q-card flat bordered class="q-mb-lg" :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'">
          <div class="flex justify-between q-pa-md">
            <div>
              Recommended Unit Mix
              <div class="text-caption">
                Use similar properties in location, type, size and quality to recommend a unit mix.
                <template v-if="marketStats?.count_units && marketStats?.count_properties">
                  Based on {{ marketStats?.count_properties }} buildings and
                  {{ marketStats?.count_units }} units.
                </template>
              </div>
            </div>
            <q-btn
              v-if="recommendedUnitMixes !== null"
              color="primary"
              icon="bolt"
              label="Use Recommended"
              no-caps
              flat
              dense
              @click="useRecommendedUnitMix"
            />
          </div>
          <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
            <div class="text-center">
              <q-spinner-grid color="primary" size="2em" />
              <div class="q-mt-sm">
                Finding similar buildings in type, size, quality among the market ...
              </div>
            </div>
          </div>
          <q-markup-table
            v-else-if="recommendedUnitMixes !== null"
            flat
            dense
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
          >
            <thead>
              <tr>
                <th scope="col" class="text-left">Recommended Unit Mix</th>
                <th scope="col" class="text-right">Avg Days on Market</th>
                <th scope="col" class="text-right">Units</th>
                <th scope="col" class="text-right">SF</th>
                <th scope="col" class="text-right">Rent</th>
                <th scope="col" class="text-right">Rent PSF</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="recommendedUnitMixes.length === 0">
                <tr>
                  <td colspan="5" class="text-center">No more recommendations</td>
                </tr>
              </template>
              <tr v-for="um in recommendedUnitMixes" v-else :key="um.name">
                <td class="text-left">{{ um.name }}</td>
                <td class="text-right">{{ formatNumber(um.avg_days_on_market) }}</td>
                <td class="text-right">
                  {{ formatNumber(um.units) }}
                  <span v-if="numberUnits">({{ formatPercent(um.units / numberUnits) }})</span>
                </td>
                <td class="text-right">{{ formatNumber(um.avg_sqft) }}</td>
                <td class="text-right">{{ formatMoney(um.avg_rent) }}</td>
                <td class="text-right">
                  <span v-if="um.avg_rent && um.avg_sqft">
                    {{ formatMoney(um.avg_rent / um.avg_sqft, 2) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card>
        <q-card flat bordered>
          <div
            class="q-pa-md flex justify-between"
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
          >
            <div class="text-h6">Floorplans</div>
            <div class="q-gutter-x-sm row">
              <q-btn
                :flat="!$q.screen.gt.xs"
                icon="add"
                :label="$q.screen.gt.xs ? 'New Floorplan' : ''"
                :class="{ 'width-150': $q.screen.gt.xs }"
                dense
                :outline="$q.screen.gt.xs"
                no-caps
                unelevated
                @click="newFloorplan"
              />
            </div>
          </div>
          <q-table
            :rows="floorplans"
            bordered
            :columns="[
              { name: 'beds', label: '# Beds', field: 'beds', align: 'left' },
              { name: 'baths', label: '# Baths', field: 'baths', align: 'left' },
              { name: 'name', label: 'Floorplan Name', field: 'name', align: 'left' },
              { name: 'units', label: '# Units', field: 'units', align: 'left' },
              {
                name: 'avg_sqft',
                label: 'Avg SF',
                field: 'avg_sqft',
                align: 'left'
              },
              {
                name: 'avg_rent',
                label: 'Avg Rent',
                field: 'avg_rent',
                align: 'left'
              },
              {
                name: 'actions',
                label: '',
                field: 'actions'
              }
            ]"
            :rows-per-page-options="[0]"
            :hide-bottom="!!floorplans.length"
            row-key="beds"
            flat
            dense
          >
            <template #no-data>
              <div class="full-width q-my-md text-center">
                <q-btn
                  color="primary"
                  icon="add"
                  label="New Floorplan"
                  class="width-150"
                  dense
                  outline
                  no-caps
                  unelevated
                  @click="newFloorplan"
                />
              </div>
            </template>
            <template #body="props">
              <q-tr :props="props">
                <q-td key="beds" :props="props">
                  <q-input
                    :ref="`beds-${props.rowIndex}`"
                    v-model.number="props.row.beds"
                    dense
                    outlined
                    type="number"
                    lazy-rules
                    :rules="[(val) => parseInt(val) >= 0 && Number.isInteger(val)]"
                    hide-bottom-space
                  />
                </q-td>
                <q-td key="baths" :props="props">
                  <q-input
                    :ref="`baths-${props.rowIndex}`"
                    v-model.number="props.row.baths"
                    dense
                    outlined
                    type="number"
                    lazy-rules
                    :rules="[(val) => parseInt(val) > 0]"
                    hide-bottom-space
                  />
                </q-td>
                <q-td key="name" :props="props">
                  <q-input v-model="props.row.name" dense outlined />
                </q-td>
                <q-td key="units" :props="props">
                  <q-input
                    :ref="`units-${props.rowIndex}`"
                    v-model.number="props.row.units"
                    dense
                    outlined
                    type="number"
                    lazy-rules
                    :rules="[(val) => parseInt(val) > 0 && Number.isInteger(val)]"
                    hide-bottom-space
                  />
                </q-td>
                <q-td key="avg_sqft" :props="props">
                  <q-input
                    :ref="`avg_sqft-${props.rowIndex}`"
                    v-model.number="props.row.avg_sqft"
                    dense
                    outlined
                    type="number"
                    lazy-rules
                    :rules="[(val) => parseInt(val) > 100 && Number.isInteger(val)]"
                    hide-bottom-space
                  />
                </q-td>
                <q-td key="avg_rent" :props="props">
                  <q-input
                    :ref="`avg_rent-${props.rowIndex}`"
                    v-model.number="props.row.avg_rent"
                    dense
                    outlined
                    type="number"
                    lazy-rules
                    :rules="[(val) => !val || parseInt(val) > 100]"
                    hide-bottom-space
                  />
                </q-td>
                <q-td key="actions" :props="props">
                  <div class="row">
                    <q-btn
                      class="col q-mr-sm"
                      color="primary"
                      icon="content_copy"
                      dense
                      flat
                      round
                      @click="duplicateFloorplan(props.rowIndex)"
                    />
                    <q-btn
                      class="col"
                      color="red"
                      icon="delete"
                      dense
                      flat
                      round
                      @click="deleteFloorplan(props.rowIndex)"
                    />
                  </div>
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </q-card>
      </q-card-section>
    </q-card>

    <q-card bordered flat class="full-width">
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Select Amenities</div>
          <div class="text-caption">
            Use similar properties in location, type, size and quality to recommend building and
            unit amenities.
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <div class="row q-mt-sm q-gutter-x-md">
          <q-card flat bordered class="col">
            <q-table
              :rows="recommendedBuildingAmenities"
              :columns="amenityColumns"
              row-key="name"
              dense
              bordered
              virtual-scroll
              :rows-per-page-options="[0]"
              :pagination="{ rowsPerPage: 0 }"
              table-style="max-height: 500px"
            >
              <template #top>
                <div class="flex justify-between full-width">
                  <div class="q-my-auto">Building Amenities</div>
                  <q-btn
                    color="primary"
                    icon="bolt"
                    label="Use Recommended"
                    no-caps
                    flat
                    dense
                    :disabled="recommendedBuildingAmenities.length === 0"
                    @click="useRecommendedBuildingAmenities"
                  />
                </div>
              </template>
              <template #body="props">
                <q-tr :props="props" dense>
                  <q-td key="name" :props="props">
                    {{ formatKey(props.row.name) }}
                  </q-td>
                  <q-td key="frequency" :props="props">
                    {{ formatPercent(props.row.frequency, 0) }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      v-if="buildingAmenities.includes(props.row.name)"
                      color="green"
                      icon="check"
                      flat
                      round
                      @click="
                        buildingAmenities = buildingAmenities.filter((_) => _ !== props.row.name)
                      "
                    />
                    <q-btn
                      v-else
                      color="primary"
                      icon="add"
                      flat
                      round
                      @click="buildingAmenities.push(props.row.name)"
                    />
                  </q-td>
                </q-tr>
              </template>
              <template #no-data>
                <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
                  <div class="text-center">
                    <q-spinner-grid color="primary" size="2em" />
                    <div class="q-mt-sm">
                      Finding similar buildings in type, size, quality among the market ...
                    </div>
                  </div>
                </div>
                <div v-else class="full-width q-my-md text-center">
                  We were not able to find any recommended building amenities for this property.
                </div>
              </template>
            </q-table>
          </q-card>
          <q-card flat bordered class="col">
            <q-table
              :rows="recommendedUnitAmenities"
              :columns="amenityColumns"
              row-key="name"
              dense
              bordered
              virtual-scroll
              :rows-per-page-options="[0]"
              :pagination="{ rowsPerPage: 0 }"
              table-style="max-height: 500px"
            >
              <template #top>
                <div class="flex justify-between full-width">
                  <div class="q-my-auto">Unit Amenities</div>
                  <div>
                    <q-btn
                      color="primary"
                      icon="bolt"
                      label="Use Recommended"
                      no-caps
                      flat
                      dense
                      :disabled="recommendedUnitAmenities.length === 0"
                      @click="useRecommendedUnitAmenities"
                    />
                  </div>
                </div>
              </template>
              <template #body="props">
                <q-tr :props="props">
                  <q-td key="name" :props="props">
                    {{ formatKey(props.row.name) }}
                  </q-td>
                  <q-td key="frequency" :props="props">
                    {{ formatPercent(props.row.frequency, 0) }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      v-if="unitAmenities.includes(props.row.name)"
                      color="green"
                      icon="check"
                      flat
                      round
                      @click="unitAmenities = unitAmenities.filter((_) => _ !== props.row.name)"
                    />
                    <q-btn
                      v-else
                      color="primary"
                      icon="add"
                      flat
                      round
                      @click="unitAmenities.push(props.row.name)"
                    />
                  </q-td>
                </q-tr>
              </template>
              <template #no-data>
                <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
                  <div class="text-center">
                    <q-spinner-grid color="primary" size="2em" />
                    <div class="q-mt-sm">
                      Finding similar buildings in type, size, quality among the market ...
                    </div>
                  </div>
                </div>
                <div v-else class="full-width q-my-md text-center">
                  We were not able to find any recommended unit amenities for this property.
                </div>
              </template>
            </q-table>
          </q-card>
        </div>
      </q-card-section>
    </q-card>

    <div class="q-pa-md row justify-center">
      <q-space />
      <q-btn no-caps flat unelevated :to="{ name: 'Dashboard' }" class="q-mr-md"> Cancel </q-btn>
      <q-btn
        :label="gotoEditNext ? 'Save & Continue' : gotoReportIdNext ? 'Save & View Report' : 'Save'"
        no-caps
        :loading="savingNewProperty"
        color="primary"
        type="submit"
        unelevated
        :disable="missingField !== null"
        @click="submit"
      >
        <q-tooltip v-if="missingField" class="text-caption"> Missing {{ missingField }} </q-tooltip>
      </q-btn>
    </div>

    <safe-teleport to="#toolbar-portal">
      <q-toolbar
        class="q-px-sm row justify-center"
        :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
        style="height: 75px"
        :style="`border-bottom: 1px solid ${
          $q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'
        }`"
      >
        <div class="parent-card full-width row justify-end q-gutter-x-sm">
          <div style="font-size: 20px; font-weight: 500">New Development</div>
          <q-space />
          <q-btn no-caps flat unelevated :to="{ name: 'Dashboard' }" class="q-mr-md">
            Cancel
          </q-btn>
          <q-btn
            :label="
              gotoEditNext ? 'Save & Continue' : gotoReportIdNext ? 'Save & View Report' : 'Save'
            "
            no-caps
            :loading="savingNewProperty"
            color="primary"
            type="submit"
            unelevated
            :disable="missingField !== null"
            @click="submit"
          >
            <q-tooltip v-if="missingField" class="text-caption">
              Missing {{ missingField }}
            </q-tooltip>
          </q-btn>
        </div>
      </q-toolbar>
    </safe-teleport>
  </div>
  <div v-else class="q-pa-md row justify-center">
    <q-inner-loading :visible="loading">
      <q-spinner-gears color="primary" size="2em" />
      <div class="q-mt-sm">Loading property information ...</div>
    </q-inner-loading>
  </div>
</template>

<script lang="ts">
import { QInput, type QTableColumn } from 'quasar'

import {
  type BuildingAmenity,
  type MarketStatsResponse,
  type RentSourceUnitInsert,
  type UnitAmenity,
  estimateUnitMix
} from 'common'
import rentsourceApi from '@/api/rentsource'
import { useRentsourceStore } from '@/stores/rentsource'
import UnitMixCsvImport from '@/components/UnitMixCsvImport.vue'
import AddressGoogleAutocomplete from '@/components/PropertyReports/AddressGoogleAutocomplete.vue'
import { formatKey, formatMoney, formatNumber, formatPercent } from '@/utils'

type Floorplan = {
  beds: number
  baths: number
  name: string
  units: number
  avg_sqft: number | string | null
  avg_rent: number | string | null
  avg_days_on_market: number | null
}

const isInteger = (val: number | null): boolean => {
  if (val === null) {
    return true
  }
  return Number.isInteger(val)
}

const cleanNumber = (val: number | string | null): number | null => {
  switch (typeof val) {
    case 'number':
      return val
    case 'string':
      if (val === '') {
        return null
      }
      return parseInt(val)
    default:
      return null
  }
}

export default {
  name: 'CardEditProperty',
  components: { AddressGoogleAutocomplete, UnitMixCsvImport },
  props: {
    gotoEditNext: {
      type: Boolean,
      description:
        'If set to true, the user will be redirected to finish editing a report after creating a new property',
      default: false
    },
    existingPropertyId: {
      type: String as () => string | null,
      description: 'The ID of the existing property to edit',
      default: null
    },
    gotoReportIdNext: {
      type: Number as () => number | null,
      description:
        'If set to a value, the user will be redirected to view that report after updating the custom property',
      default: null
    }
  },
  data() {
    return {
      isInteger,
      formatMoney,
      formatNumber,
      formatPercent,
      loading: false,
      assetType: 'Multifamily',
      savingNewProperty: false,
      location: {
        address: '',
        street_address: '',
        city: '',
        state: '',
        zip: '',
        lat: null as number | null,
        lon: null as number | null
      },
      buildingName: null as string | null,
      yearBuilt: null as number | null,
      numberUnits: null as number | null,
      numberStories: null as number | null,
      avgQuality: 0.85 as number | null,
      floorplans: [] as Floorplan[],
      marketStats: null as MarketStatsResponse | null,
      buildingAmenities: [] as BuildingAmenity[],
      unitAmenities: [] as UnitAmenity[],
      loadingMarketStats: false,
      amenityColumns: [
        { name: 'name', label: 'Amenity', field: (_) => formatKey(_.name), align: 'left' },
        {
          name: 'frequency',
          label: 'Market Frequency',
          field: (_) => formatPercent(_.frequency, 0),
          align: 'right'
        },
        { name: 'actions', label: 'Action', field: 'actions' }
      ] as QTableColumn[],
      unitMixCsvImportDialog: false
    }
  },
  computed: {
    missingField(): string | null {
      if (this.location.lat === null || this.location.lon === null) {
        return 'Address'
      }

      if (!this.location.zip) {
        return 'Zip Code'
      }

      if (!this.location.street_address) {
        return 'Street Address'
      }

      if (!this.location.city) {
        return 'City'
      }

      if (!this.location.state) {
        return 'State'
      }

      if (this.yearBuilt === null) {
        return 'Year Built'
      }

      if (this.numberUnits === null) {
        return 'Number of Units'
      }

      if (this.numberStories === null) {
        return 'Number of Stories'
      }

      if (this.avgQuality === null) {
        return 'Overall Quality'
      }

      if (this.floorplans.length === 0) {
        return 'Floorplans'
      }

      return null
    },
    recommendedUnitMixes(): Floorplan[] | null {
      // we use the market stats (avg_unit_mix) to offer a list of viable unit mixes
      const subjectNumberUnits = this.numberUnits
      if (this.marketStats === null || subjectNumberUnits === null) {
        return null
      }
      const unitMixes = this.marketStats.avg_unit_mix
      if (unitMixes.length === 0) {
        return null
      }
      return unitMixes
        .filter((_) => this.floorplans.every((f) => f.name !== `Market ${_.beds}BR/${_.baths}BA`))
        .sort((a, b) => a.beds - b.beds || a.baths - b.baths)
        .map((unitMix) => {
          return {
            beds: unitMix.beds,
            baths: unitMix.baths,
            name: `${unitMix.beds}BR/${unitMix.baths}BA`,
            units: Math.max(Math.round(unitMix.frequency * subjectNumberUnits), 1),
            avg_sqft: unitMix.avg_sqft,
            avg_rent: unitMix.avg_rent,
            avg_days_on_market: unitMix.avg_days_on_market ?? null
          }
        })
    },
    recommendedBuildingAmenities(): { name: string; frequency: number }[] {
      if (this.marketStats === null || this.marketStats.building_amenity_frequencies === null) {
        return []
      }
      return Object.entries(this.marketStats.building_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.05)
        .sort(([, a], [, b]) => b - a)
        .map(([amenity, frequency]) => ({ name: amenity, frequency }))
    },
    recommendedUnitAmenities(): { name: string; frequency: number }[] {
      if (this.marketStats === null || this.marketStats.unit_amenity_frequencies === null) {
        return []
      }
      return Object.entries(this.marketStats.unit_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.05)
        .sort(([, a], [, b]) => b - a)
        .map(([amenity, frequency]) => ({ name: amenity, frequency }))
    }
  },
  created() {
    if (this.existingPropertyId) {
      this.loading = true
      useRentsourceStore()
        .details(this.existingPropertyId)
        .then((data) => {
          this.location = {
            address: data.street_address,
            street_address: data.street_address,
            city: data.city,
            state: data.state,
            zip: data.zip_code,
            lat: data.lat,
            lon: data.lon
          }
          this.buildingName = data.building_name
          this.yearBuilt = data.year_built
          this.numberUnits = data.number_units
          this.numberStories = data.number_stories
          this.avgQuality = data.building_quality.property_overall_quality
          this.assetType = data.is_single_family
            ? 'Single Family'
            : data.is_apartment
              ? 'Multifamily'
              : 'Condo'

          const estimatedUnitMix = estimateUnitMix(data, 'floorplan')
          this.floorplans = estimatedUnitMix.map((unit) => ({
            beds: unit.bed,
            baths: unit.bath,
            name: unit.key || `${unit.bed}BR/${unit.bath}BA`,
            units: unit.estimatedUnits || 0,
            avg_sqft: unit.avgSqft || null,
            avg_rent: unit.avgEffectiveRent || null,
            avg_days_on_market: unit.avgDaysOnMarket || null
          }))
          this.buildingAmenities = data.building_amenities || []
          this.unitAmenities = data.unit_amenities || []
        })
        .finally(() => {
          this.loading = false
          // wait for a tick before getting market stats
          this.$nextTick(() => {
            this.getMarketStats()
          })
        })
    }
  },
  methods: {
    formatKey,
    useRecommendedBuildingAmenities() {
      if (this.marketStats === null || this.marketStats.building_amenity_frequencies === null) {
        return null
      }

      this.buildingAmenities = Object.entries(this.marketStats.building_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.2)
        .map(([amenity]) => amenity as BuildingAmenity)

      this.$tracker.usedRecommendedAmenities('building')
    },
    useRecommendedUnitAmenities() {
      if (this.marketStats === null || this.marketStats.unit_amenity_frequencies === null) {
        return null
      }

      this.unitAmenities = Object.entries(this.marketStats.unit_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.2)
        .map(([amenity]) => amenity as UnitAmenity)

      this.$tracker.usedRecommendedAmenities('unit')
    },
    useRecommendedUnitMix() {
      if (this.recommendedUnitMixes === null) {
        return
      }
      this.floorplans = this.recommendedUnitMixes
      this.$tracker.usedRecommendedUnitMix()
    },
    getMarketStats() {
      if (this.location.lat === null || this.location.lon === null) {
        return
      }
      if (
        (this.yearBuilt && !(this.$refs['yearBuiltInput'] as QInput).validate()) ||
        (this.numberUnits && !(this.$refs['numberUnitsInput'] as QInput).validate()) ||
        (this.numberStories && !(this.$refs['numberStoriesInput'] as QInput).validate())
      ) {
        return
      }
      this.loadingMarketStats = true
      this.marketStats = null
      useRentsourceStore()
        .marketStatistics({
          lat: this.location.lat,
          lon: this.location.lon,
          is_single_family: this.assetType === 'Single Family',
          is_apartment: this.assetType === 'Multifamily',
          is_condo: this.assetType === 'Condo',
          year_built: this.yearBuilt || null,
          number_units: this.numberUnits || null,
          number_stories: this.numberStories || null,
          avg_quality_prediction: this.avgQuality
        })
        .then((stats) => {
          this.marketStats = stats
        })
        .finally(() => {
          this.loadingMarketStats = false
        })
    },
    duplicateFloorplan(idx: number) {
      this.floorplans.push({ ...this.floorplans[idx] })
    },
    selectedAddress(place: google.maps.places.PlaceResult) {
      this.location.lat = place.geometry?.location?.lat() || null
      this.location.lon = place.geometry?.location?.lng() || null
      const geoLookup: {
        [key: string]: { short_name: string; long_name: string }
      } = {}
      place.address_components?.forEach((g) => {
        g.types.forEach((t) => {
          geoLookup[t] = g
        })
      })
      const getStreetNumber = () => {
        return (geoLookup['street_number'] || {})['long_name']
      }
      const getStreetName = () => {
        return (geoLookup['route'] || {})['long_name']
      }
      const getCity = () => {
        const a = geoLookup['locality'] || {}
        const b = geoLookup['sublocality'] || {}
        const c = geoLookup['sublocality_level_1'] || {}
        const d = geoLookup['neighborhood'] || {}
        return a['long_name'] || b['long_name'] || c['long_name'] || d['long_name']
      }
      const getState = () => {
        return (geoLookup['administrative_area_level_1'] || {})['short_name']
      }
      const getZipCode = () => {
        return (geoLookup['postal_code'] || {})['short_name']
      }
      this.location.address = place.formatted_address || ''
      this.location.street_address = `${getStreetNumber()} ${getStreetName()}`
      this.location.city = getCity()
      this.location.state = getState()
      this.location.zip = getZipCode()
      this.getMarketStats()
    },
    deleteFloorplan(idx: number) {
      this.floorplans = this.floorplans.filter((_, i) => i !== idx)
    },
    gotoNextPage(id: string) {
      // TODO: If killing the overview page validated, simplify this logic
      if (this.gotoEditNext) {
        this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
      } else if (this.gotoReportIdNext) {
        this.$router.push({ name: 'Property Report', params: { reportId: this.gotoReportIdNext } })
      } else {
        this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
      }
    },
    newFloorplan() {
      this.floorplans.push({
        beds: 0,
        baths: 1,
        name: '',
        units: 1,
        avg_sqft: null,
        avg_rent: null,
        avg_days_on_market: null
      })
    },
    submit() {
      const validateRefs = [
        'autocompleteRef',
        'yearBuiltInput',
        'numberUnitsInput',
        'numberStoriesInput'
      ]
      this.floorplans.forEach((_, index) => {
        validateRefs.push(`beds-${index}`)
        validateRefs.push(`baths-${index}`)
        validateRefs.push(`units-${index}`)
        validateRefs.push(`avg_sqft-${index}`)
        validateRefs.push(`avg_rent-${index}`)
      })
      const validations = validateRefs
        .map((ref) => (this.$refs[ref] as QInput).validate())
        .filter((_) => !_)
      if (validations.length) {
        return
      }
      if (this.missingField !== null) {
        return
      }
      this.savingNewProperty = true

      if (!this.existingPropertyId) {
        rentsourceApi
          .insertProperty({
            street_address: this.location.street_address,
            city: this.location.city,
            state: this.location.state,
            zip_code: this.location.zip,
            lat: this.location.lat!,
            lon: this.location.lon!,
            year_built: this.yearBuilt!,
            number_units: this.numberUnits!,
            number_stories: this.numberStories!,
            is_single_family: this.assetType === 'Single Family',
            is_apartment: this.assetType === 'Multifamily',
            is_condo: this.assetType === 'Condo',
            building_amenities: this.buildingAmenities,
            unit_amenities: this.unitAmenities,
            building_name: this.buildingName,
            avg_quality: this.avgQuality!,
            units: this.floorplans.flatMap((floorplan) =>
              new Array(Math.round(floorplan.units)).fill({
                bed: cleanNumber(floorplan.beds),
                bath: floorplan.baths,
                sqft: cleanNumber(floorplan.avg_sqft),
                price: cleanNumber(floorplan.avg_rent),
                name: floorplan.name
              } as RentSourceUnitInsert)
            )
          })
          .then((newID: { id: string } | null) => {
            if (newID !== null) {
              this.gotoNextPage(newID.id)
            }
          })
          .catch((e) => {
            console.error(e)
            this.$q.notify({
              message:
                'An error occurred while saving the property. We are aware of the issue and are working to resolve it. Please try again later.',
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.savingNewProperty = false
          })
      } else {
        rentsourceApi
          .updateProperty(this.existingPropertyId, {
            street_address: this.location.street_address,
            city: this.location.city,
            state: this.location.state,
            zip_code: this.location.zip,
            lat: this.location.lat!,
            lon: this.location.lon!,
            year_built: this.yearBuilt!,
            number_units: this.numberUnits!,
            number_stories: this.numberStories!,
            is_single_family: this.assetType === 'Single Family',
            is_apartment: this.assetType === 'Multifamily',
            is_condo: this.assetType === 'Condo',
            building_amenities: this.buildingAmenities,
            unit_amenities: this.unitAmenities,
            building_name: this.buildingName,
            avg_quality: this.avgQuality!,
            units: this.floorplans.flatMap((floorplan) =>
              new Array(Math.round(floorplan.units)).fill({
                bed: floorplan.beds,
                bath: floorplan.baths,
                sqft: cleanNumber(floorplan.avg_sqft),
                price: cleanNumber(floorplan.avg_rent),
                name: floorplan.name
              } as RentSourceUnitInsert)
            )
          })
          .then(() => {
            this.gotoNextPage(this.existingPropertyId!)
          })
          .catch((e: Error) => {
            console.error(e)
            this.$q.notify({
              message:
                'An error occurred while saving the property. We are aware of the issue and are working to resolve it. Please try again later.',
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.savingNewProperty = false
          })
      }
    },
    unitMixCsvImportFinished(floorplans: Floorplan[]) {
      this.unitMixCsvImportDialog = false
      this.floorplans = this.floorplans.concat(floorplans)
      this.$q.notify({
        message: `CSV Upload Completed: ${floorplans.length} units added`,
        color: 'positive',
        position: 'top'
      })
    }
  }
}
</script>
<style scoped>
.parent-card {
  max-width: 1600px;
  margin: auto;
}
.width-150 {
  width: 150px;
}
</style>
